import { useState, useEffect } from "react";
import { useScrollReveal } from "../../hooks/useScrollReveal";

// ── Scroll reveal ─────────────────────────────────────────────────────────────

function Reveal({ children, delay = 0, style = {} }) {
  const [ref, visible] = useScrollReveal(delay);
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── Animated signal feed ──────────────────────────────────────────────────────

const LIVE_SIGNALS = [
  { ticker: "AAPL", action: "BUY",  conf: 91, change: "+2.4%", sector: "Tech" },
  { ticker: "NVDA", action: "BUY",  conf: 88, change: "+5.2%", sector: "Tech" },
  { ticker: "TSLA", action: "HOLD", conf: 72, change: "+0.8%", sector: "EV"   },
  { ticker: "META", action: "BUY",  conf: 84, change: "+3.1%", sector: "Tech" },
  { ticker: "AMZN", action: "BUY",  conf: 79, change: "+1.9%", sector: "Retail"},
];

function SignalFeed() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActive(a => (a + 1) % LIVE_SIGNALS.length), 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {LIVE_SIGNALS.map(({ ticker, action, conf, change }, i) => (
        <div
          key={ticker}
          style={{
            display: "flex", alignItems: "center",
            padding: "8px 12px", borderRadius: 10,
            background: i === active
              ? "rgba(16,183,127,0.07)"
              : "rgba(255,255,255,0.022)",
            border: i === active
              ? "1px solid rgba(16,183,127,0.2)"
              : "1px solid rgba(255,255,255,0.05)",
            transition: "all 0.4s ease",
            gap: 10,
          }}
        >
          <span style={{
            width: 42, fontSize: "0.73rem", fontWeight: 700,
            color: "#F0EEF6", fontFamily: "Space Grotesk, sans-serif",
          }}>{ticker}</span>

          <span style={{
            fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.04em",
            padding: "2px 7px", borderRadius: 999,
            background: action === "BUY" ? "rgba(16,183,127,0.12)" : "rgba(251,191,36,0.12)",
            border: `1px solid ${action === "BUY" ? "rgba(16,183,127,0.25)" : "rgba(251,191,36,0.25)"}`,
            color: action === "BUY" ? "#10B77F" : "#FBBF24",
            flexShrink: 0,
          }}>{action}</span>

          <div style={{
            flex: 1, height: 2, borderRadius: 99,
            background: "rgba(255,255,255,0.06)", overflow: "hidden",
          }}>
            <div style={{
              height: "100%",
              width: `${conf}%`,
              borderRadius: 99,
              background: action === "BUY" ? "#10B77F" : "#FBBF24",
              transition: "width 0.6s ease",
            }} />
          </div>

          <span style={{
            fontSize: "0.65rem", color: "rgba(255,255,255,0.32)",
            fontFamily: "Space Grotesk, sans-serif", flexShrink: 0,
          }}>{conf}%</span>

          <span style={{
            fontSize: "0.7rem", fontWeight: 700, color: "#10B77F",
            fontFamily: "Space Grotesk, sans-serif", flexShrink: 0, width: 40, textAlign: "right",
          }}>{change}</span>
        </div>
      ))}
    </div>
  );
}

// ── Mini sparkline ────────────────────────────────────────────────────────────

function MiniSparkline({ color = "#10B77F" }) {
  return (
    <svg width="100%" height="52" viewBox="0 0 260 52" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`bsg-${color.replace("#","")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0"   />
        </linearGradient>
      </defs>
      <path
        d="M0,52 L0,42 C20,36 38,40 58,32 C78,24 98,28 118,20 C138,12 158,16 178,10 C198,5 218,7 240,3 L260,0 L260,52 Z"
        fill={`url(#bsg-${color.replace("#","")})`}
      />
      <path
        d="M0,42 C20,36 38,40 58,32 C78,24 98,28 118,20 C138,12 158,16 178,10 C198,5 218,7 240,3 L260,0"
        fill="none" stroke={color} strokeWidth="2" strokeLinecap="round"
      />
      <circle cx="260" cy="0" r="4" fill={color} />
      <circle cx="260" cy="0" r="8" fill={color} fillOpacity="0.2" />
    </svg>
  );
}

// ── Risk gauge ────────────────────────────────────────────────────────────────

function RiskGauge({ score = 72 }) {
  const pct = score / 100;
  const r = 44;
  const circ = 2 * Math.PI * r;
  const dash = pct * circ * 0.75;

  return (
    <div style={{ position: "relative", width: 120, height: 80, margin: "0 auto" }}>
      <svg width="120" height="80" viewBox="0 0 120 80">
        <circle cx="60" cy="70" r={r} fill="none"
          stroke="rgba(255,255,255,0.06)" strokeWidth="8"
          strokeDasharray={`${circ * 0.75} ${circ}`}
          strokeDashoffset={0}
          strokeLinecap="round"
          transform="rotate(-225 60 70)"
        />
        <circle cx="60" cy="70" r={r} fill="none"
          stroke="url(#riskGrad)" strokeWidth="8"
          strokeDasharray={`${dash} ${circ}`}
          strokeDashoffset={0}
          strokeLinecap="round"
          transform="rotate(-225 60 70)"
        />
        <defs>
          <linearGradient id="riskGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="#10B77F" />
            <stop offset="100%" stopColor="#FBBF24" />
          </linearGradient>
        </defs>
      </svg>
      <div style={{
        position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)",
        textAlign: "center",
      }}>
        <div style={{
          fontSize: "1.5rem", fontWeight: 900, color: "#F0EEF6",
          fontFamily: "Space Grotesk, sans-serif", letterSpacing: "-0.04em", lineHeight: 1,
        }}>{score}</div>
        <div style={{
          fontSize: "0.58rem", color: "rgba(255,255,255,0.3)",
          fontFamily: "Space Grotesk, sans-serif", textTransform: "uppercase",
          letterSpacing: "0.1em", marginTop: 2,
        }}>Risk Score</div>
      </div>
    </div>
  );
}

// ── Card wrapper ──────────────────────────────────────────────────────────────

function BentoCard({ children, style = {}, glowColor, hover = true }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => hover && setHovered(true)}
      onMouseLeave={() => hover && setHovered(false)}
      style={{
        background: hovered && glowColor
          ? `rgba(255,255,255,0.038)`
          : "rgba(255,255,255,0.03)",
        border: hovered && glowColor
          ? `1px solid ${glowColor}55`
          : "1px solid rgba(255,255,255,0.1)",
        borderRadius: 20,
        padding: "28px",
        position: "relative",
        overflow: "hidden",
        transition: "border-color 0.3s ease, box-shadow 0.3s ease, background 0.3s ease",
        boxShadow: hovered && glowColor
          ? `0 0 60px ${glowColor}20, 0 0 0 1px ${glowColor}15, inset 0 1px 0 rgba(255,255,255,0.1)`
          : "inset 0 1px 0 rgba(255,255,255,0.07), 0 1px 40px rgba(0,0,0,0.3)",
        ...style,
      }}
    >
      {/* Subtle top edge highlight */}
      <div style={{
        position: "absolute", top: 0, left: "10%", right: "10%", height: 1,
        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)",
        pointerEvents: "none",
      }} />
      {children}
    </div>
  );
}

// ── Section ───────────────────────────────────────────────────────────────────

export default function BentoSection() {
  return (
    <section style={{
      padding: "80px 24px 100px",
      position: "relative",
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>

        {/* Header */}
        <Reveal>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div style={{
              fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.18em",
              textTransform: "uppercase", color: "#10B77F",
              fontFamily: "Space Grotesk, sans-serif",
              marginBottom: 14,
            }}>
              Platform Features
            </div>
            <h2 style={{
              fontFamily: "Space Grotesk, sans-serif",
              fontSize: "clamp(1.9rem, 3.5vw, 2.9rem)",
              fontWeight: 800, letterSpacing: "-0.03em",
              color: "#F0EEF6", lineHeight: 1.12, marginBottom: 14,
            }}>
              Everything you need to invest smarter
            </h2>
            <p style={{
              fontSize: "1rem", color: "rgba(240,238,246,0.4)",
              maxWidth: 480, margin: "0 auto",
              fontFamily: "Space Grotesk, sans-serif", lineHeight: 1.7,
            }}>
              Three pillars that give retail investors an institutional edge
              — without the institutional price tag.
            </p>
          </div>
        </Reveal>

        {/* ── Row 1: wide + narrow ── */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1.5fr 1fr",
          gap: 16, marginBottom: 16,
        }} className="bento-row-1">

          {/* AI Signals — wide */}
          <Reveal delay={60}>
            <BentoCard glowColor="#10B77F" style={{ height: "100%" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 11,
                  background: "rgba(16,183,127,0.1)",
                  border: "1px solid rgba(16,183,127,0.2)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "1.1rem",
                }}>🤖</div>
                <div>
                  <div style={{
                    fontSize: "0.88rem", fontWeight: 700, color: "#F0EEF6",
                    fontFamily: "Space Grotesk, sans-serif",
                  }}>Signals that see the market clearly</div>
                  <div style={{
                    fontSize: "0.7rem", color: "rgba(255,255,255,0.35)",
                    fontFamily: "Space Grotesk, sans-serif",
                  }}>Real-time · Updated every 90 seconds</div>
                </div>
                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: "#10B77F", animation: "pulseGold 1.6s ease-in-out infinite",
                  }} />
                  <span style={{
                    fontSize: "0.65rem", color: "#10B77F",
                    fontFamily: "Space Grotesk, sans-serif",
                  }}>Live</span>
                </div>
              </div>
              <SignalFeed />
            </BentoCard>
          </Reveal>

          {/* Portfolio — narrow */}
          <Reveal delay={120}>
            <BentoCard glowColor="#60A5FA" style={{ height: "100%" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 11,
                  background: "rgba(96,165,250,0.1)",
                  border: "1px solid rgba(96,165,250,0.2)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "1.1rem",
                }}>📊</div>
                <div>
                  <div style={{
                    fontSize: "0.88rem", fontWeight: 700, color: "#F0EEF6",
                    fontFamily: "Space Grotesk, sans-serif",
                  }}>Understand your true exposure</div>
                  <div style={{
                    fontSize: "0.7rem", color: "rgba(255,255,255,0.35)",
                    fontFamily: "Space Grotesk, sans-serif",
                  }}>Institutional-grade · 30-day view</div>
                </div>
              </div>

              <div style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.05)",
                borderRadius: 12, padding: "12px 14px",
                marginBottom: 14,
              }}>
                <div style={{
                  fontSize: "0.58rem", color: "rgba(255,255,255,0.28)",
                  fontFamily: "Space Grotesk, sans-serif",
                  textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6,
                }}>Total Return</div>
                <div style={{
                  fontSize: "2rem", fontWeight: 900, color: "#F0EEF6",
                  fontFamily: "Space Grotesk, sans-serif",
                  letterSpacing: "-0.04em", lineHeight: 1, marginBottom: 4,
                }}>+18.4%</div>
                <MiniSparkline color="#60A5FA" />
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                {[["Day", "+0.8%", "#10B77F"], ["Week", "+3.2%", "#10B77F"], ["Month", "+18.4%", "#60A5FA"]].map(
                  ([label, val, color]) => (
                    <div key={label} style={{
                      flex: 1, padding: "8px 10px", borderRadius: 10,
                      background: "rgba(255,255,255,0.025)",
                      border: "1px solid rgba(255,255,255,0.06)",
                      textAlign: "center",
                    }}>
                      <div style={{
                        fontSize: "0.57rem", color: "rgba(255,255,255,0.28)",
                        fontFamily: "Space Grotesk, sans-serif", marginBottom: 3,
                      }}>{label}</div>
                      <div style={{
                        fontSize: "0.82rem", fontWeight: 700, color,
                        fontFamily: "Space Grotesk, sans-serif",
                      }}>{val}</div>
                    </div>
                  )
                )}
              </div>
            </BentoCard>
          </Reveal>
        </div>

        {/* ── Row 2: three equal cards ── */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 16,
        }} className="bento-row-2">

          {/* Risk Management */}
          <Reveal delay={100}>
            <BentoCard glowColor="#FBBF24" style={{ textAlign: "center" }}>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "4px 12px",
                background: "rgba(251,191,36,0.08)",
                border: "1px solid rgba(251,191,36,0.18)",
                borderRadius: 999, marginBottom: 20,
              }}>
                <span style={{ fontSize: "0.7rem" }}>⚡</span>
                <span style={{
                  fontSize: "0.7rem", fontWeight: 600,
                  color: "#FBBF24", fontFamily: "Space Grotesk, sans-serif",
                }}>Risk Management</span>
              </div>
              <RiskGauge score={72} />
              <p style={{
                fontSize: "0.78rem", color: "rgba(240,238,246,0.38)",
                fontFamily: "Space Grotesk, sans-serif",
                lineHeight: 1.65, marginTop: 16,
              }}>
                Deep insight into your holdings, concentration risk, and performance attribution — institutional-grade tools.
              </p>
            </BentoCard>
          </Reveal>

          {/* Market Intelligence */}
          <Reveal delay={160}>
            <BentoCard glowColor="#A78BFA">
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 11,
                  background: "rgba(167,139,250,0.1)",
                  border: "1px solid rgba(167,139,250,0.2)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "1.1rem",
                }}>🌐</div>
                <div>
                  <div style={{
                    fontSize: "0.88rem", fontWeight: 700, color: "#F0EEF6",
                    fontFamily: "Space Grotesk, sans-serif",
                  }}>Stay ahead of the market</div>
                  <div style={{
                    fontSize: "0.7rem", color: "rgba(255,255,255,0.35)",
                    fontFamily: "Space Grotesk, sans-serif",
                  }}>Macro · Earnings · Sector rotation</div>
                </div>
              </div>

              {[
                { label: "S&P 500",    val: "+0.42%", up: true  },
                { label: "NASDAQ",     val: "+0.71%", up: true  },
                { label: "10Y Yield",  val: "4.32%",  up: false },
                { label: "VIX",        val: "14.8",   up: false },
              ].map(({ label, val, up }) => (
                <div key={label} style={{
                  display: "flex", justifyContent: "space-between",
                  padding: "6px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.04)",
                }}>
                  <span style={{
                    fontSize: "0.72rem", color: "rgba(255,255,255,0.4)",
                    fontFamily: "Space Grotesk, sans-serif",
                  }}>{label}</span>
                  <span style={{
                    fontSize: "0.72rem", fontWeight: 700,
                    color: up ? "#10B77F" : "rgba(255,255,255,0.55)",
                    fontFamily: "Space Grotesk, sans-serif",
                  }}>{val}</span>
                </div>
              ))}
            </BentoCard>
          </Reveal>

          {/* Security */}
          <Reveal delay={220}>
            <BentoCard glowColor="#10B77F">
              <div style={{ marginBottom: 20 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 13,
                  background: "rgba(16,183,127,0.1)",
                  border: "1px solid rgba(16,183,127,0.2)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "1.3rem", marginBottom: 14,
                }}>🔐</div>
                <div style={{
                  fontSize: "0.92rem", fontWeight: 700, color: "#F0EEF6",
                  fontFamily: "Space Grotesk, sans-serif", marginBottom: 8,
                }}>Earnings analysis, macro trends, sector rotation, and news sentiment — unified into an AI-curated feed.</div>
                <p style={{
                  fontSize: "0.78rem", color: "rgba(240,238,246,0.38)",
                  fontFamily: "Space Grotesk, sans-serif", lineHeight: 1.65,
                }}>
                  Tailored to your positions and updated in real time — so you never miss a move that matters.
                </p>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {["SOC 2 Type II", "256-bit AES encryption", "Read-only API access"].map(item => (
                  <div key={item} style={{
                    display: "flex", alignItems: "center", gap: 8,
                  }}>
                    <span style={{
                      width: 16, height: 16, borderRadius: "50%",
                      background: "rgba(16,183,127,0.12)",
                      border: "1px solid rgba(16,183,127,0.25)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "0.55rem", color: "#10B77F", flexShrink: 0,
                    }}>✓</span>
                    <span style={{
                      fontSize: "0.73rem", color: "rgba(255,255,255,0.45)",
                      fontFamily: "Space Grotesk, sans-serif",
                    }}>{item}</span>
                  </div>
                ))}
              </div>
            </BentoCard>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
